import styled from 'styled-components';

import { ANIM, COLORS, DESKTOP, SPACE } from '../../../../theme/tokens';
import { Text } from '../../../../components';

export const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[24]};
`;

export const Intro = styled(Text)`
  font-size: 13px;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

export const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

/** Horizontal hairline (white 50%) separating the form's sections. */
export const Divider = styled.hr`
  height: 1px;
  margin: 0;
  border: none;
  background: ${COLORS.settingsDivider};
`;

export const GroupTitle = styled(Text)`
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
`;

/** Two-up field grid on desktop, single column on mobile. */
export const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACE[16]};

  ${DESKTOP} {
    grid-template-columns: 1fr 1fr;
  }
`;

export const FieldCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/** A cell spanning the full grid width (e.g. street address). */
export const FullCell = styled(FieldCell)`
  ${DESKTOP} {
    grid-column: 1 / -1;
  }
`;

export const FieldError = styled(Text)`
  font-size: 13px;
  color: ${COLORS.statusDanger};
`;

export const DateLabel = styled(Text)`
  font-size: 14px;
  color: ${COLORS.text};
`;

export const DateInput = styled.input<{ $invalid: boolean }>`
  padding: 14px ${SPACE[16]};
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$invalid ? COLORS.statusDanger : COLORS.panelBorder)};
  background: ${COLORS.surfaceInset};
  color: ${COLORS.text};
  font-size: 16px;
  font-family: inherit;
  outline: none;
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  /* The browser's outline is dropped, so the border says it has the focus. An invalid date stays red. */
  &:focus {
    border-color: ${(p) => (p.$invalid ? COLORS.statusDanger : COLORS.accent)};
  }

  &::-webkit-calendar-picker-indicator {
    filter: invert(0.7);
    cursor: pointer;
  }
`;

export const SaveError = styled(Text)`
  font-size: 13px;
  color: ${COLORS.statusDanger};
`;

/** A selectable payout-method row (bank / crypto) with its own on/off switch. */
export const MethodRow = styled.div<{ $on: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[16]};
  padding: ${SPACE[16]};
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$on ? COLORS.accentMagenta : COLORS.panelBorder)};
  background: ${COLORS.surfaceInset};
`;

export const MethodText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
`;

export const MethodTitle = styled(Text)`
  font-size: 14px;
  color: ${COLORS.text};
`;

export const MethodDesc = styled(Text)`
  font-size: 13px;
  line-height: 1.45;
  color: ${COLORS.textMuted};
`;

/** Highlighted advisory (e.g. the crypto destination-tag warning). */
export const Notice = styled(Text)`
  font-size: 13px;
  line-height: 1.5;
  color: ${COLORS.warning};
`;

export const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};

  ${DESKTOP} {
    align-items: center;
    border-top: 1px solid ${COLORS.settingsDivider};
    padding-top: ${SPACE[20]};

    && > * {
      width: 50%;
    }
  }
`;
